import { useEffect, useMemo } from 'react'
import { useProfile } from './useProfile'
import { useAppSelector } from '@/redux'
import { useFirebaseUser } from './useFirebaseUser'
import { analytics } from '../../analytics'

export function useAuthState() {
  const firebaseUser = useFirebaseUser()
  const profile = useProfile()

  const profileMatchesUser = useMemo(() => {
    if (!profile.data) return false

    const hasMatchingId = String(profile.data.id) === String(firebaseUser?.uid)
    const hasMatchingEmail = String(profile.data.email) === String(firebaseUser?.email)

    return hasMatchingId || hasMatchingEmail
  }, [profile.data, firebaseUser])

  const loginResolved =
    firebaseUser === undefined ? false : !!firebaseUser?.uid ? profileMatchesUser || profile.isError : true

  const authFinished = useAppSelector(store => store.AppStatus.authFinished)
  const initialAuthResolved = useAppSelector(store => store.AppStatus.hasResolvedInitialAuth)

  return {
    isLoggedIn: profileMatchesUser,
    loginResolved,
    authFinished,
    initialAuthResolved,
  }
}

export function useAutoLogin() {
  const firebaseUser = useFirebaseUser()
  const profile = useProfile({ enabled: true, track: true })

  const profileMatchesUser = useMemo(() => {
    if (!profile.data) return false

    const hasMatchingId = String(profile.data.id) === String(firebaseUser?.uid)
    const hasMatchingEmail = String(profile.data.email) === String(firebaseUser?.email)

    return hasMatchingId || hasMatchingEmail
  }, [profile.data, firebaseUser])

  useEffect(() => {
    if (!!profile.data && profileMatchesUser) {
      analytics.identify({ user: profile.data, isLoggedIn: true })
      analytics?.track('logged_in_pageview')
    }
  }, [profileMatchesUser])

  return {
    isLoggedIn: profileMatchesUser,
    profile: profile.data,
  }
}
